import {
  Box,
  Card,
  CardContent,
  Divider,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import languages from '../../../../../../helpers/utils/languages';
import { getCurrencySymbol } from '../Retractions/FeesCalculations';

const StepThree = ({ formData }) => {
  const tableCellStyle = {
    fontWeight: 'bold',
    fontSize: '0.7em',
    padding: '8px 4px',
  };

  const compactCellStyle = {
    padding: '4px 8px',
    fontSize: '0.8rem',
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Récapitulatif du devis
      </Typography>

      {/* Section Informations Client */}
      <Box container sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ width: '48%' }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Informations client
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nom :</strong> {formData.firstName} {formData.lastName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email :</strong> {formData.email}
            </Typography>
            <Typography variant="body2">
              <strong>Téléphone :</strong> {formData.phone}
            </Typography>
          </CardContent>
        </Box>

        <Card variant="outlined" sx={{ width: '48%' }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Adresse de facturation
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Adresse :</strong> {formData.billingAddress?.address1}
            </Typography>
            {formData.billingAddress?.address2 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Complément :</strong>{' '}
                {formData.billingAddress?.address2}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Ville :</strong> {formData.billingAddress?.city}
            </Typography>
            <Typography variant="body2">
              <strong>Code postal :</strong>{' '}
              {formData.billingAddress?.postalCode}
            </Typography>
            {formData.billingAddress?.country && (
              <Typography
                variant="body2"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <strong>Pays :</strong>
                <span
                  className={`fi fi-${formData.billingAddress.country.toLowerCase()}`}
                />
                {languages.find(
                  (country) => country.code2 === formData.billingAddress.country
                )?.country || formData.billingAddress.country}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section Détails de la commande */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Détails de la commande
      </Typography>

      {formData.lines && formData.lines.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="left"
                  >
                    Référence - Titre
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="center"
                  >
                    Quantité
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="right"
                  >
                    Prix unitaire HT
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="right"
                  >
                    Taux de TVA
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="right"
                  >
                    Prix unitaire TTC
                  </TableCell>
                  <TableCell
                    sx={{ ...tableCellStyle, padding: '8px 4px' }}
                    align="right"
                  >
                    Total ligne HT
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {formData.lines.map((line) => (
                  <StyledTableRow key={line.reference}>
                    <TableCell sx={compactCellStyle}>
                      <strong>{line.reference}</strong> - {line.title}
                    </TableCell>
                    <TableCell sx={compactCellStyle} align="center">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {line.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCellStyle} align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {line.priceHt.toFixed(2)} €
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCellStyle} align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {line.tvaValue}%
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCellStyle} align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {line.priceTtc.toFixed(2)} €
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCellStyle} align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {(line.quantity * line.priceHt).toFixed(2)} €
                      </Typography>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Section Totaux */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <TableContainer component={Paper} sx={{ width: '300px' }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                      Total HT
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {`${formData.amounts?.priceHt?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                      Total TVA ({formData.amounts?.tvaValue}%)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {`${formData.amounts?.totalVat?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell
                      align="left"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1em',
                        color: 'primary.main',
                      }}
                    >
                      Total TTC
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '1.1em',
                        color: 'primary.main',
                      }}
                    >
                      {`${formData.amounts?.priceTtc?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>
                      Réduction ({formData.amounts?.discount}%)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {`${formData.amounts?.discountAmount?.toFixed(2)} ${getCurrencySymbol(formData?.currency)}`}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Aucun produit ajouté au devis
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default StepThree;
